import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Company pages
import Login     from "./pages/Login";
import Layout    from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import RateCard  from "./pages/RateCard";
import LiveStock from "./pages/LiveStock";
import SaleLogs  from "./pages/SaleLogs";

import {
  GasPurchase, GasSale, GodownTransfer,
  AdjustmentEntry, PaymentEntry, RefillBullet,
} from "./pages/EntryPages";

import {
  PurchaseLogs, PaymentsLog, PendingPayments, CustomersVendors,
  RateAnalysis,
} from "./pages/LogsPages";

import { CustomerPerformance } from "./pages/CustomerPerformance";
import { MTPending }           from "./pages/MTTracking";
import {
  CalendarPage,
  StockMovement, AdjustmentLog, DailyClosing, Downloads,
  AttendancePage, MonthlySummary, MastersPage, AuditLogs, SalesMarketing,
} from "./pages/StubPages";

// New real pages (cloned from reference)
import { ExpensesPage }              from "./pages/ExpensesPage";
import { DieselPage }                from "./pages/DieselPage";
import { VehiclesPage }              from "./pages/VehiclesPage";
import { ChallansPage  as Challans } from "./pages/ChallansPage";
import { ChallanGapsPage as ChallanGaps } from "./pages/ChallanGapsPage";
import { PendingCylindersPage as PendingCylinders } from "./pages/PendingCylindersPage";
import { TeamChatPage }   from "./pages/TeamChatPage";
import { ChecklistPage }  from "./pages/ChecklistPage";
import { EmployeesFullPage, EmployeeLedgerPage } from "./pages/EmployeesPage";
import { DailyTallyPage } from "./pages/DailyTallyPage";

// Batch 3 — new features
import PendingBillsPage       from "./pages/PendingBillsPage";
import PhysicalRegisterPage   from "./pages/PhysicalRegisterPage";
import OrderPredictionsPage   from "./pages/OrderPredictionsPage";
import PaymentFollowupsPage   from "./pages/PaymentFollowupsPage";
import SaleCalculatorPage     from "./pages/SaleCalculatorPage";
import BulkPurchaseEntryPage  from "./pages/BulkPurchaseEntryPage";

// Super admin pages
import SuperAdminLogin     from "./pages/superadmin/SuperAdminLogin";
import SuperAdminDashboard from "./pages/superadmin/SuperAdminDashboard";
import CustomerLedger      from "./pages/CustomerLedger";

// ── Spinner ──────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-800 rounded-full animate-spin" />
    </div>
  );
}

// ── Protected company app routes ──────────────────────────────────────────────
function CompanyApp() {
  const { isLoggedIn, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  return (
    <Layout>
      <Routes>
        <Route path="/"                      element={<Dashboard />} />
        <Route path="/rate-card"             element={<RateCard />} />
        <Route path="/rate-analysis"         element={<RateAnalysis />} />

        <Route path="/calendar"              element={<CalendarPage />} />
        <Route path="/checklist"             element={<ChecklistPage />} />
        <Route path="/chat"                  element={<TeamChatPage />} />

        <Route path="/stock-view"            element={<LiveStock />} />
        <Route path="/logs/sales"            element={<SaleLogs />} />
        <Route path="/performance"           element={<CustomerPerformance />} />
        <Route path="/logs/purchases"        element={<PurchaseLogs />} />
        <Route path="/payments-log"          element={<PaymentsLog />} />

        <Route path="/entry/purchase"        element={<GasPurchase />} />
        <Route path="/entry/sale"            element={<GasSale />} />
        <Route path="/entry/transfer"        element={<GodownTransfer />} />
        <Route path="/entry/adjustment"      element={<AdjustmentEntry />} />
        <Route path="/entry/bulk-purchase"   element={<BulkPurchaseEntryPage />} />
        <Route path="/entry/refill"          element={<RefillBullet />} />
        <Route path="/entry/payment"         element={<PaymentEntry />} />

        <Route path="/expenses"              element={<ExpensesPage />} />
        <Route path="/diesel"                element={<DieselPage />} />
        <Route path="/vehicles"              element={<VehiclesPage />} />

        <Route path="/pending/mt"            element={<MTPending />} />
        <Route path="/pending/payments"      element={<PendingPayments />} />
        <Route path="/pending/bills"         element={<PendingBillsPage />} />
        <Route path="/pending/cylinders"     element={<PendingCylinders />} />

        <Route path="/parties"               element={<CustomersVendors />} />
        <Route path="/customers/:customerId/ledger" element={<CustomerLedger />} />
        <Route path="/register"              element={<PhysicalRegisterPage />} />
        <Route path="/followups"             element={<PaymentFollowupsPage />} />
        <Route path="/predictions"           element={<OrderPredictionsPage />} />
        <Route path="/sale-calculator"       element={<SaleCalculatorPage />} />

        <Route path="/challans"              element={<Challans />} />
        <Route path="/challans/gaps"         element={<ChallanGaps />} />

        <Route path="/tally"                 element={<DailyTallyPage />} />
        <Route path="/stock-movement"        element={<StockMovement />} />
        <Route path="/adjustments/log"       element={<AdjustmentLog />} />
        <Route path="/reports/daily-closing" element={<DailyClosing />} />
        <Route path="/downloads"             element={<Downloads />} />

        <Route path="/attendance"            element={<AttendancePage />} />
        <Route path="/attendance/summary"    element={<MonthlySummary />} />
        <Route path="/employees"             element={<EmployeesFullPage />} />
        <Route path="/employees/:employeeId/ledger" element={<EmployeeLedgerPage />} />

        <Route path="/masters"               element={<MastersPage />} />
        <Route path="/audit"                 element={<AuditLogs />} />
        <Route path="/sales"                 element={<SalesMarketing />} />

        <Route path="*"                      element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

// ── Super admin protected route ───────────────────────────────────────────────
function SARoute({ children }) {
  const { isSuperAdmin, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!isSuperAdmin) return <Navigate to="/superadmin/login" replace />;
  return children;
}

// ── Root app ──────────────────────────────────────────────────────────────────
function AppRoutes() {
  const { isLoggedIn, isSuperAdmin, loading } = useAuth();

  if (loading) return <Spinner />;

  return (
    <Routes>
      {/* Super admin */}
      <Route path="/superadmin/login" element={
        isSuperAdmin ? <Navigate to="/superadmin" replace /> : <SuperAdminLogin />
      } />
      <Route path="/superadmin/*" element={
        <SARoute><SuperAdminDashboard /></SARoute>
      } />

      {/* Company login */}
      <Route path="/login" element={
        isLoggedIn ? <Navigate to="/" replace /> : <Login />
      } />

      {/* Company app */}
      <Route path="/*" element={<CompanyApp />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
